import { Button, Group, Stack, Text } from "@assetwatch/ui";
import { useEffect, useRef, useState } from "react";
import type { ProcedureCategoryOption } from "../../../queries/procedureAuthoringApi";

const categoryTagClasses: Record<string, string> = {
  Maintenance: "bg-primary-container text-primary-on-container",
  Reliability: "bg-secondary-container text-secondary-on-container",
  Operations: "bg-tertiary-container text-tertiary-on-container",
  Safety: "bg-error-container text-error-on-container",
};

export function ProcedureCategoryTagPicker({
  categories: selected,
  options,
  error,
  errorKey,
  editable = true,
  onChange,
}: {
  categories: string[];
  options: ProcedureCategoryOption[];
  error?: string;
  errorKey?: string;
  editable?: boolean;
  onChange: (categories: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const pickerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const available = options
    .map((option) => option.name)
    .filter((value) => !selected.includes(value));

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (open && available.length > 0) {
      optionRefs.current[activeIndex]?.focus();
    }
  }, [activeIndex, available.length, open]);

  const closePicker = () => {
    setOpen(false);
    document.getElementById("procedure-category-trigger")?.focus();
  };

  const selectCategory = (category: string) => {
    onChange([...selected, category]);
    closePicker();
  };

  return (
    <Stack gap={4}>
      <Group align="center" gap={8} wrap="wrap">
        {selected.map((category) => (
          <span
            className={`inline-flex items-center gap-(--spacing-4) rounded-(--radius-full) px-(--spacing-8) py-(--spacing-4) text-(length:--font-size-label-small) ${categoryTagClasses[category] ?? "bg-surface-container text-text-default"}`}
            key={category}
          >
            {category}
            {editable && selected.length > 1 ? (
              <button
                aria-label={`Remove ${category} category`}
                className="rounded-full text-text-subtle hover:text-text-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-button-focus-ring"
                onClick={() =>
                  onChange(selected.filter((item) => item !== category))
                }
                type="button"
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
        {editable ? (
          <div className="relative" ref={pickerRef}>
            <Button
              aria-controls="procedure-category-options"
              aria-expanded={open}
              data-procedure-error={errorKey}
              id="procedure-category-trigger"
              onClick={() => {
                setActiveIndex(0);
                setOpen((current) => !current);
              }}
              size="sm"
              variant="ghost"
            >
              + Add category
            </Button>
            {open ? (
              <div
                aria-activedescendant={
                  available.length > 0
                    ? `procedure-category-option-${activeIndex}`
                    : undefined
                }
                aria-label="Available categories"
                className="absolute left-0 top-full z-(--z-layer-popover) mt-(--spacing-4) min-w-48 overflow-hidden rounded-(--radius-sm) border border-outline-subtle bg-surface shadow-elevation-2"
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    closePicker();
                    return;
                  }
                  if (!available.length) return;
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    setActiveIndex((current) =>
                      current === available.length - 1 ? 0 : current + 1,
                    );
                  } else if (event.key === "ArrowUp") {
                    event.preventDefault();
                    setActiveIndex((current) =>
                      current === 0 ? available.length - 1 : current - 1,
                    );
                  } else if (event.key === "Home") {
                    event.preventDefault();
                    setActiveIndex(0);
                  } else if (event.key === "End") {
                    event.preventDefault();
                    setActiveIndex(available.length - 1);
                  }
                }}
                id="procedure-category-options"
                role="listbox"
                tabIndex={-1}
              >
                {available.length ? (
                  available.map((category, index) => (
                    <button
                      aria-selected={false}
                      className="block w-full px-(--spacing-12) py-(--spacing-8) text-left text-text-default hover:bg-surface-container-low focus-visible:bg-surface-container-low focus-visible:outline-none"
                      id={`procedure-category-option-${index}`}
                      key={category}
                      onClick={() => selectCategory(category)}
                      ref={(element) => {
                        optionRefs.current[index] = element;
                      }}
                      role="option"
                      type="button"
                    >
                      {category}
                    </button>
                  ))
                ) : (
                  <div className="px-(--spacing-12) py-(--spacing-8)">
                    <Text tone="subtle">No more categories</Text>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </Group>
      {editable && error ? <Text tone="negative">{error}</Text> : null}
    </Stack>
  );
}
